import { AsyncLocalStorage } from "node:async_hooks";
import { sql } from "drizzle-orm";

import { sessionKeyToGucName } from "./rls-session.js";

/**
 * RLS DB wrapper — execution paths for `withRlsConnection`:
 * 1. **Reuse outer Drizzle transaction** (`reuseOuterConnection`): same connection, apply GUCs then run `fn`.
 * 2. **Bun SQL** (`session.client.begin`): short transaction around `fn`.
 * 3. **PGlite / drivers with `transaction()`**: delegate to driver helper.
 * 4. **node-pg Pool** (`connect` + `query`): lease a client, BEGIN → set_config → `fn` → COMMIT, or ROLLBACK on error, then `release()`.
 */

/** Flat session record: each key becomes one transaction-local GUC via {@link sessionKeyToGucName}. */
import type { RlsSessionContext } from "./rls-context-contract.js";

type RlsDrizzleDatabaseLike = {
  session: unknown;
  _: { session?: unknown };
  transaction: (
    callback: (tx: unknown) => unknown | Promise<unknown>,
    ...rest: unknown[]
  ) => Promise<unknown>;
};

/** Ordered `(name, value)` pairs for `set_config(name, value, true)`. */
function rlsSetConfigPairs(rls: RlsSessionContext): [string, string][] {
  const pairs: [string, string][] = [];
  for (const [sessionKey, value] of Object.entries(rls)) {
    pairs.push([sessionKeyToGucName(sessionKey), value]);
  }
  return pairs;
}

async function forEachSetConfig(
  rls: RlsSessionContext,
  setOne: (name: string, value: string) => Promise<void>,
): Promise<void> {
  for (const [name, value] of rlsSetConfigPairs(rls)) {
    await setOne(name, value);
  }
}

/**
 * While a prepared query runs, nested queries reuse this client (same outer `begin` / tx).
 */
const rlsExecClient = new AsyncLocalStorage<unknown>();

function isBunSqlSession(session: unknown): boolean {
  const name = (session as { constructor?: { name?: string } })?.constructor?.name;
  return typeof name === "string" && name.includes("BunSQLSession");
}

function isDrizzleTransactionDb(db: unknown): boolean {
  const d = db as { constructor?: { name?: string }; rollback?: unknown };
  if (typeof d?.rollback === "function") {
    return true;
  }
  const name = d?.constructor?.name;
  return typeof name === "string" && name.includes("Transaction");
}

async function execSetConfig(client: any, name: string, value: string): Promise<void> {
  if (typeof client?.unsafe === "function") {
    await client.unsafe(`select set_config($1::text, $2::text, true)`, [name, value]);
    return;
  }
  if (typeof client?.query === "function") {
    await client.query(`select set_config($1::text, $2::text, true)`, [name, value]);
    return;
  }
  throw new Error("createRlsDb: unsupported SQL driver (expected Bun SQL, node-pg client/pool, or PGlite)");
}

async function applyRlsSessionVars(client: any, rls: RlsSessionContext): Promise<void> {
  await forEachSetConfig(rls, (name, value) => execSetConfig(client, name, value));
}

async function injectRlsVarsOnTx(tx: any, rls: RlsSessionContext): Promise<void> {
  await forEachSetConfig(rls, (name, value) => tx.execute(sql`SELECT set_config(${name}, ${value}, true)`));
}

/**
 * pg `Pool.connect()`-style client: BEGIN → apply RLS GUCs → `fn` → COMMIT on success,
 * or ROLLBACK if anything fails (including failed COMMIT). Always `release()` in `finally`.
 *
 * @internal Exported only for unit tests — not re-exported from `@gencow/core` public API.
 */
export async function withRlsLeasedConnection<T>(
  leased: { query: (sql: string) => Promise<unknown>; release: () => void },
  rls: RlsSessionContext,
  fn: (client: { query: (sql: string) => Promise<unknown>; release: () => void }) => Promise<T>,
): Promise<T> {
  try {
    await leased.query("begin");
    await applyRlsSessionVars(leased, rls);
    const result = await rlsExecClient.run(leased, () => fn(leased));
    await leased.query("commit");
    return result;
  } catch (e) {
    try {
      await leased.query("rollback");
    } catch {
      /* ignore */
    }
    throw e;
  } finally {
    leased.release();
  }
}

/**
 * Runs `fn` with a connection that has transaction-local RLS session variables set.
 * Pool / autocommit: opens a short transaction (Bun `begin`, PGlite `transaction`).
 * When `db` is already a Drizzle transaction object, reuses that connection (no nested top-level tx).
 */
async function withRlsConnection<T>(
  session: any,
  rls: RlsSessionContext,
  reuseOuterConnection: boolean,
  fn: (client: any) => Promise<T>,
): Promise<T> {
  if (reuseOuterConnection) {
    const c = session.client;
    return rlsExecClient.run(c, async () => {
      await applyRlsSessionVars(c, rls);
      return fn(c);
    });
  }
  const c = session.client;
  const runInner = async (client: any) => {
    await applyRlsSessionVars(client, rls);
    return rlsExecClient.run(client, () => fn(client));
  };
  if (typeof c.begin === "function") {
    return c.begin(runInner);
  }
  if (typeof c.transaction === "function") {
    return c.transaction(runInner);
  }
  if (typeof c.connect === "function" && typeof c.query === "function") {
    const leased = await c.connect();
    return withRlsLeasedConnection(leased, rls, fn);
  }
  return runInner(c);
}

function createClientBoundSession(session: any, client: unknown) {
  return new Proxy(session, {
    get(target, prop, receiver) {
      if (prop === "client") {
        return client;
      }
      const value = Reflect.get(target, prop, receiver);
      return typeof value === "function" ? value.bind(receiver) : value;
    },
  });
}

type PreparedQueryClientExecutor = (client: unknown, placeholderValues?: unknown) => Promise<unknown>;

type PreparedQueryClientExecutors = {
  execute: PreparedQueryClientExecutor;
  all?: PreparedQueryClientExecutor;
};

async function withPreparedQueryClient<T>(pq: any, client: unknown, fn: () => Promise<T>): Promise<T> {
  if (!("client" in pq)) {
    return await fn();
  }

  const prevPq = pq.client;
  pq.client = client;
  try {
    return await fn();
  } finally {
    pq.client = prevPq;
  }
}

function createGenericPreparedQueryClientExecutors(pq: any): PreparedQueryClientExecutors {
  const origExecute = pq.execute.bind(pq);
  const origAll = typeof pq.all === "function" ? pq.all.bind(pq) : null;

  return {
    execute(client, placeholderValues) {
      return withPreparedQueryClient(pq, client, () => origExecute(placeholderValues));
    },
    all: origAll
      ? (client, placeholderValues) => withPreparedQueryClient(pq, client, () => origAll(placeholderValues))
      : undefined,
  };
}

function createBunSqlPreparedQueryClientExecutors(
  baseSession: any,
  prepareQuery: (...args: unknown[]) => unknown,
  prepareArgs: unknown[],
): PreparedQueryClientExecutors {
  const reprepareOnClient = (client: unknown) => {
    const clientBoundSession = createClientBoundSession(baseSession, client);
    return Reflect.apply(prepareQuery, clientBoundSession, prepareArgs) as {
      execute: (values?: unknown) => Promise<unknown>;
      all?: (values?: unknown) => Promise<unknown>;
    };
  };

  return {
    execute(client, placeholderValues) {
      return reprepareOnClient(client).execute(placeholderValues);
    },
    all(client, placeholderValues) {
      const txPreparedQuery = reprepareOnClient(client);
      if (typeof txPreparedQuery.all === "function") {
        return txPreparedQuery.all(placeholderValues);
      }
      return txPreparedQuery.execute(placeholderValues);
    },
  };
}

function createPreparedQueryClientExecutors(params: {
  pq: any;
  baseSession: any;
  prepareQuery: (...args: unknown[]) => unknown;
  prepareArgs: unknown[];
}): PreparedQueryClientExecutors {
  if (isBunSqlSession(params.baseSession)) {
    return createBunSqlPreparedQueryClientExecutors(params.baseSession, params.prepareQuery, params.prepareArgs);
  }
  return createGenericPreparedQueryClientExecutors(params.pq);
}

async function runPreparedQueryWithRls(
  session: any,
  rls: RlsSessionContext,
  reuseOuterConnection: boolean,
  runOnClient: PreparedQueryClientExecutor,
  placeholderValues?: unknown,
): Promise<unknown> {
  const active = rlsExecClient.getStore();
  if (active) {
    return runOnClient(active, placeholderValues);
  }
  return withRlsConnection(session, rls, reuseOuterConnection, (client) => runOnClient(client, placeholderValues));
}

function wrapPreparedQuery(
  pq: any,
  session: any,
  baseSession: any,
  prepareQuery: (...args: unknown[]) => unknown,
  prepareArgs: unknown[],
  rls: RlsSessionContext,
  reuseOuterConnection: boolean,
) {
  const clientExecutors = createPreparedQueryClientExecutors({
    pq,
    baseSession,
    prepareQuery,
    prepareArgs,
  });

  pq.execute = (placeholderValues?: unknown) =>
    runPreparedQueryWithRls(session, rls, reuseOuterConnection, clientExecutors.execute, placeholderValues);

  if (clientExecutors.all) {
    pq.all = (placeholderValues?: unknown) =>
      runPreparedQueryWithRls(session, rls, reuseOuterConnection, clientExecutors.all!, placeholderValues);
  }
}

function wrapSession(session: any, rls: RlsSessionContext, reuseOuterConnection: boolean) {
  return new Proxy(session, {
    get(sTarget, sProp, sRecv) {
      if (sProp === "client") {
        return rlsExecClient.getStore() ?? Reflect.get(sTarget, sProp, sRecv);
      }
      if (sProp === "prepareQuery") {
        return (...args: unknown[]) => {
          const prepareQuery = Reflect.get(sTarget, sProp, sRecv);
          const pq = Reflect.apply(prepareQuery, sRecv, args);
          wrapPreparedQuery(pq, sRecv, sTarget, prepareQuery, args, rls, reuseOuterConnection);
          return pq;
        };
      }
      const v = Reflect.get(sTarget, sProp, sRecv);
      return typeof v === "function" ? v.bind(sTarget) : v;
    },
  });
}

/**
 * RLS-scoped Drizzle handle: every query path (`select`, `insert`, `execute`, …) runs
 * `set_config` for the given context in the same DB transaction as the query
 * (PgBouncer–safe transaction-local GUCs).
 *
 * `db.transaction()` still injects the same variables at the start of the callback transaction.
 */
export function createRlsDb<TDb extends RlsDrizzleDatabaseLike>(db: TDb, rls: RlsSessionContext): TDb {
  const reuseOuterConnection = isDrizzleTransactionDb(db);
  const baseSession = (db as unknown as { session: any }).session;
  const wrappedSession = wrapSession(baseSession, rls, reuseOuterConnection);

  return new Proxy(db, {
    get(target, prop, receiver) {
      if (prop === "session") {
        return wrappedSession;
      }
      if (prop === "_") {
        const inner = target._;
        return new Proxy(inner, {
          get(i, p, r) {
            if (p === "session") return wrappedSession;
            return Reflect.get(i, p, r);
          },
        });
      }
      if (prop === "transaction") {
        return async (callback: any, ...rest: any[]) => {
          return await target.transaction(
            async (tx: any) => {
              await injectRlsVarsOnTx(tx, rls);
              return await callback(tx);
            },
            ...(rest as any),
          );
        };
      }
      const value = Reflect.get(target, prop, receiver);
      return typeof value === "function" ? value.bind(receiver) : value;
    },
  });
}
